import React, { useEffect, useRef, useState } from 'react';
import useStateWithCallback from "use-state-with-callback";
import { IAGrid } from '../../../Components/IInterfaces';
import { CommonUtil } from '../../../utils/CommonUtil';
import { NetworkManager } from '../../../Components/NetworkManager';
import { Constants } from '../../Languages/Constants';
import BrokerStatusRenderer from '../../InterchangeApplication/BrokerStatusRenderer';
import InspectionGrid from '../../InspectionGrid/InspectionGrid';
import GenericPopup from '../../../Components/GenericPopup';
import InspectionDetails from './InspectionDetails';
import { useNavigate } from 'react-router';
import InspectionRenderer from '../../InspectionGrid/InspectionRenderer';
import Offcanvas from '../../../Components/OffCanvas';
import InspectedImages from './InspectedImages';
import ToastManager from '../../../Components/ToastManager';
import Loader from '../../../Components/Loader';

const DocumentTypeGrid = ({docTypeId}: any) => {
  const navigate = useNavigate();
  const saasOrgId = CommonUtil.getSaasOrgId();
  const [popupInfo, setPopupInfo] = useState<any>("");
  const [documentId, setDocumentId] = useState<any>(null);
  const [imagesData, setImagesData] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [imageCount, setImageCount] = useState<number>(0);
  const userGridRef = useRef<IAGrid>(null);

  const [users, setUsers] = useStateWithCallback([], () => {
    if (userGridRef.current) {
      userGridRef.current.columnFittoSize();
    }
  });

  const onLoginError = (err: any) => {
    console.error("Login Error:", err);
  };

  useEffect(() => {
    getAllDetails();
  }, [docTypeId]);

  useEffect(() => {
    getDocInspection();
  }, [documentId]);

  const closePopup = () => {
    setPopupInfo("")
    setDocumentId(null);
  };

  const getAllDetails = () => {
    setLoading(true);
    const orgUrl = `api/dashboard/doc-filters?orgId=${saasOrgId}&docTypeId=${docTypeId}&status=DONE,IN_PROGRESS,TO_BE_PROCESS`;
    NetworkManager.sendJsonRequest(orgUrl, "", "", "GET", false, onGetAllDetails, onLoginError);
  };
  
  const onGetAllDetails = (dataObj: any) => {
    if (dataObj) {
      const restData = JSON.parse(dataObj);
      const sorted: any = [...(Array.isArray(restData) ? restData : [restData])]
        .sort((a, b) => (b.documentId || 0) - (a.documentId || 0));
        setUsers(sorted);
      }
      setLoading(false);
  };

  useEffect(() => {
    if(popupInfo === false){
    getAllDetails();
  }
  }, [popupInfo]);

  const getDocInspection = () => {
    if (!documentId) {
      return;
    }
    setLoading(true);
    const orgUrl = `api/dashboard/compare?orgId=${saasOrgId}&isToCompareDifferentDocs=false&docId=${documentId}`;
    NetworkManager.sendJsonRequest(orgUrl, "", "", "GET", false, onDocInspection, onLoginError);
  };
  
  const onDocInspection = (dataObj: any) => {
    const restData = JSON.parse(dataObj);
    if (restData && !restData.errorMessage) {
      if (restData?.inspectionDetails?.length) {
        documentImages(restData.inspectionDetails);
        setLoading(false);
      }
    } else {
      ToastManager.showToast(restData?.errorMessage || "Unknown error", "Error");
    }
  }

  const documentImages = (data: any[]) => {
    const images = data?.map(document => ({
      documentId: document.documentId,
      docTypeName: document.docTypeName || undefined,
      documentCode: document.documentCode,
      vinNumber: document?.vinNumber,
      brand: document?.brand,
      unitNumber: document?.unitNumber,
      licenseNumber: document?.licenseNumber,
      saasOrgId: document.saasOrgId,
      Images: document.processDetails?.flatMap((process: any) =>
        process.taskDetails?.flatMap((task: any) =>
          task?.images?.map((img: any) => ({img: img?.imagePath, comment: img?.comment})) || []
        )
      )
    }));

    const totalImageCount = images?.reduce((acc: number, doc: any) => {
      return acc + (doc.Images?.length || 0);
    }, 0);

    setImageCount(totalImageCount);
    setImagesData(images);
  };
  
  useEffect(() => {
    if(imageCount > 0){
      setPopupInfo({type: "ImgDownload"});
    }
  }, [imageCount]);

  const [userColumns] = useState([
    {
      field: 'documentCode',
      headerName: "Document",
      resizable: true,
      flex: 1,
      sortable: true,
      cellStyle: baseStyle,
      headerClass: 'header-white',
      filter: 'agTextColumnFilter',
      cellRenderer: InspectionRenderer
    },
    {
      field: 'equipmentType',
      headerName: "Equipment Type",
      resizable: true,
      flex: 1,
      sortable: true,
      cellStyle: baseStyle,
      headerClass: 'header-white',
      filter: 'agTextColumnFilter'
    },
    {
      field: 'unitNumber',
      headerName: "Unit Number",
      resizable: true,
      flex: 1,
      sortable: true,
      cellStyle: baseStyle,
      headerClass: 'header-white',
      filter: 'agTextColumnFilter'
    },
    {
      field: 'vinNumber',
      headerName: "Vin Number",
      resizable: true,
      flex: 1,
      sortable: true,
      cellStyle: baseStyle,
      headerClass: 'header-white',
      filter: 'agTextColumnFilter'
    },
    {
      field: 'creationDate',
      headerName: "Creation Date",
      resizable: true,
      flex: 1,
      sortable: true,
      cellStyle: baseStyle,
      headerClass: 'header-white',
      filter: 'agTextColumnFilter'
    },
    {
      field: 'createdBy',
      headerName: "Created By",
      resizable: true,
      flex: 1,
      sortable: true,
      cellStyle: baseStyle,
      headerClass: 'header-white',
      filter: 'agTextColumnFilter'
    },
    {
      field: 'carrierName',
      headerName: "Carrier Name",
      resizable: true,
      flex: 1,
      sortable: true,
      cellStyle: baseStyle,
      headerClass: 'header-white',
      filter: 'agTextColumnFilter'
    },
    {
      field: 'brokerName',
      headerName: "Broker Name",
      resizable: true,
      flex: 1,
      sortable: true,
      cellStyle: baseStyle,
      headerClass: 'header-white',
      filter: 'agTextColumnFilter'
    },
    {
      field: 'trailerReturnDuration',
      headerName: "Trailer Return Duration",
      resizable: true,
      flex: 1,
      sortable: true,
      cellStyle: baseStyle,
      headerClass: 'header-white',
      filter: 'agTextColumnFilter'
    },
    {
      field: "documentStatus",
      headerName: CommonUtil.getMessageText(Constants.STATUS, `Status`),
      resizable: true,
      flex: 1,
      sortable: true,
      cellStyle: baseStyle,
      headerClass: 'header-white',
      filter: 'agTextColumnFilter',
      cellRenderer: BrokerStatusRenderer
    },
    {
      field: "action",
      headerName: "Action",
      width: 80,
      resizable: true,
      flex: 1,
      // sortable: true,
      cellStyle: baseStyle,
      headerClass: 'header-white',
      filter: 'agTextColumnFilter',
      cellRenderer: (props: any) => {
        return (
          <div className={`${props?.data?.imageCount > 0 ? "" : "d-none"} text-center`} role='button' title={`Download & Share ${props?.data?.imageCount} Images`}>
            <img src="../../images/download-lite.svg" alt="download" style={{width: "18px"}} 
              onClick={() => onActionClick(props)} />
          </div>
        )
      }
    },
  ]);

  const onActionClick = (props: any) => {
    setDocumentId(props?.data?.documentId);
  };

  const openInspectionPopup = (data: any) => {
    setDocumentId(data?.documentId);
    localStorage.setItem("documentId", data.documentId);    
    localStorage.setItem("documentCode", data.documentCode);  
    setPopupInfo("inspectionPopup");
  };

  const onClickUserDetails = (userData:any) =>{
    localStorage.setItem("documentId", userData.documentId);    
    localStorage.setItem("documentCode", userData.documentCode);  
    navigate("/menu/inspectionDashboard/inspectionDocument");
  }

  return (
    <div className="container-fluid container-fluid-padding mt-3" style={{ backgroundColor: 'white' }}>
      <div className="row fs-7 pt-1">
        {loading && <Loader />}
        <div className="ag-theme-alpine ag-theme-customeTheme gridHeight" style={{ height: "calc(100vh - 330px)", width: "100%", paddingLeft: "8px", paddingRight: "5px" }}>
          <InspectionGrid
            cols={userColumns}
            rows={users}
            pageSize={100}
            addNew={true}
            tooltip={`Add`}
            name={`Add`}
            headerHeight={CommonUtil.GRID_HEADER_HEIGHT}
            rowHeight={CommonUtil.GRID_ROW_HEIGHT}
            ref={userGridRef}
            method={{ onClickUserDetails }}
          />
        </div>
      </div>
      {popupInfo === "inspectionPopup" && (
        <GenericPopup
          header={"Document Details"}
          mode="open"
          onClose={closePopup}
          size="xl"
        >
          <InspectionDetails documentId={documentId}/>
        </GenericPopup>
      )}
      <Offcanvas 
        show={popupInfo?.type === "ImgDownload"}
        onHide={() => {
          setPopupInfo(null);
          setDocumentId(null);
          setImagesData([]);
          setImageCount(0);
        }}
        title="Download / Share Pictures"
        size="70%"
      >
        <InspectedImages data={imagesData} imageCount={imageCount} show={popupInfo?.type === "ImgDownload"} />
      </Offcanvas>
    </div>
  );
};

const baseStyle = {
  fontWeight: '400',
  letterSpacing: '0px',
  fontSize: '13px',
  fontFamily: 'poppins',
  paddingTop: '5px'
};

export default DocumentTypeGrid;
